/**
 * Toast Context - Global toast notification management
 * 
 * Features:
 * - Global toast state management
 * - Auto-incrementing toast IDs
 * - Queue management with stacking
 * - Easy-to-use hook interface
 * 
 * Usage:
 * ```jsx
 * // In App.js or root component
 * import { ToastProvider } from './contexts/ToastContext';
 * 
 * function App() {
 *   return (
 *     <ToastProvider>
 *       <YourApp />
 *     </ToastProvider>
 *   );
 * }
 * 
 * // In any component
 * import { useToast } from './contexts/ToastContext';
 * 
 * function MyComponent() {
 *   const { showToast, showSuccess, showError, showWarning, showInfo } = useToast();
 *   
 *   const handleSave = async () => {
 *     try {
 *       await saveData();
 *       showSuccess('Data saved successfully!');
 *     } catch (error) {
 *       showError('Failed to save data');
 *     }
 *   };
 *   
 *   return <button onClick={handleSave}>Save</button>;
 * }
 * ```
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { ToastContainer, ToastTypes } from '../components/Toast';

const ToastContext = createContext();

let toastIdCounter = 0;

export const ToastProvider = ({ children, position = 'top-right', maxToasts = 5 }) => {
  const [toasts, setToasts] = useState([]);

  /**
   * Show a toast notification
   * @param {Object} options - Toast options
   * @param {string} options.type - Toast type (success, error, warning, info)
   * @param {string} options.message - Toast message
   * @param {number} [options.duration=5000] - Auto-dismiss duration in ms (0 = no auto-dismiss)
   */
  const showToast = useCallback((options) => {
    const id = ++toastIdCounter;
    const toast = {
      id,
      type: options.type || ToastTypes.INFO,
      message: options.message,
      duration: options.duration !== undefined ? options.duration : 5000
    };

    setToasts((prevToasts) => {
      const newToasts = [...prevToasts, toast];
      // Limit number of toasts shown at once
      if (newToasts.length > maxToasts) {
        return newToasts.slice(-maxToasts);
      }
      return newToasts;
    });

    return id;
  }, [maxToasts]);

  /**
   * Dismiss a specific toast
   */
  const dismissToast = useCallback((id) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  /**
   * Dismiss all toasts
   */
  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  /**
   * Convenience methods for common toast types
   */
  const showSuccess = useCallback((message, duration) => {
    return showToast({ type: ToastTypes.SUCCESS, message, duration });
  }, [showToast]);

  const showError = useCallback((message, duration) => {
    return showToast({ type: ToastTypes.ERROR, message, duration });
  }, [showToast]);

  const showWarning = useCallback((message, duration) => {
    return showToast({ type: ToastTypes.WARNING, message, duration });
  }, [showToast]);

  const showInfo = useCallback((message, duration) => {
    return showToast({ type: ToastTypes.INFO, message, duration });
  }, [showToast]);

  const value = {
    toasts,
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    dismissToast,
    dismissAll
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} position={position} />
    </ToastContext.Provider>
  );
};

/**
 * Hook to use toast notifications
 * @returns {Object} Toast methods
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;
